<script setup>
import { computed, onMounted } from 'vue';
import { useToggle } from '@vueuse/core';
import { useRoute, useRouter } from 'dashboard/composables/route';
import {
  getUserPermissions,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'dashboard/composables/useI18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import CustomAttribute from 'dashboard/components/CustomAttribute.vue';
import PanelAddButton from '../sidebar/PanelAddButton.vue';
import PanelEmptyState from '../sidebar/PanelEmptyState.vue';

const props = defineProps({
  attributeType: {
    type: String,
    default: 'conversation_attribute',
  },
  contactId: { type: Number, default: null },
  attributeFrom: {
    type: String,
    required: true,
  },
  emptyStateMessage: {
    type: String,
    default: '',
  },
  startAt: {
    type: String,
    default: 'even',
    validator: value => value === 'even' || value === 'odd',
  },
  // Desenho da #1243 para a sidebar de detalhes: repassado ao
  // `CustomAttribute.vue`, e tambem o que desliga o zebrado aqui.
  panel: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const getters = useStoreGetters();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

const [showAllAttributes, toggleShowAllAttributes] = useToggle(false);

const currentChat = computed(() => getters.getSelectedChat.value);
const attributes = computed(() =>
  getters['attributes/getAttributesByModel'].value(props.attributeType)
);

const contactIdentifier = computed(
  () =>
    currentChat.value.meta?.sender?.id ||
    route.params.contactId ||
    props.contactId
);

const contact = computed(() =>
  getters['contacts/getContact'].value(contactIdentifier.value)
);

const customAttributes = computed(() => {
  if (props.attributeType === 'conversation_attribute')
    return currentChat.value.custom_attributes || {};
  return contact.value.custom_attributes || {};
});

const conversationId = computed(() => currentChat.value.id);

const toggleButtonText = computed(() =>
  !showAllAttributes.value
    ? t('CUSTOM_ATTRIBUTES.SHOW_MORE')
    : t('CUSTOM_ATTRIBUTES.SHOW_LESS')
);

const filteredAttributes = computed(() =>
  attributes.value.map(attribute => {
    // Check if the attribute key exists in customAttributes
    const hasValue = Object.hasOwnProperty.call(
      customAttributes.value,
      attribute.attribute_key
    );
    const isCheckbox = attribute.attribute_display_type === 'checkbox';
    const defaultValue = isCheckbox ? false : '';

    return {
      ...attribute,
      // Set value from customAttributes if it exists, otherwise use default value
      value: hasValue
        ? customAttributes.value[attribute.attribute_key]
        : defaultValue,
    };
  })
);

const displayedAttributes = computed(() => {
  // Show only the first 5 attributes or all depending on showAllAttributes
  if (showAllAttributes.value || filteredAttributes.value.length <= 5) {
    return filteredAttributes.value;
  }
  return filteredAttributes.value.slice(0, 5);
});

const showMoreUISettingsKey = computed(
  () => `show_all_attributes_${props.attributeFrom}`
);

const initializeSettings = () => {
  showAllAttributes.value =
    uiSettings.value[showMoreUISettingsKey.value] || false;
};

const onClickToggle = () => {
  toggleShowAllAttributes();
  updateUISettings({
    [showMoreUISettingsKey.value]: showAllAttributes.value,
  });
};

const onUpdate = async (key, value) => {
  const updatedAttributes = { ...customAttributes.value, [key]: value };
  try {
    if (props.attributeType === 'conversation_attribute') {
      await store.dispatch('updateCustomAttributes', {
        conversationId: conversationId.value,
        customAttributes: updatedAttributes,
      });
    } else {
      store.dispatch('contacts/update', {
        id: props.contactId,
        custom_attributes: updatedAttributes,
      });
    }
    useAlert(t('CUSTOM_ATTRIBUTES.FORM.UPDATE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message || t('CUSTOM_ATTRIBUTES.FORM.UPDATE.ERROR');
    useAlert(errorMessage);
  }
};

const onDelete = async key => {
  try {
    const { [key]: remove, ...updatedAttributes } = customAttributes.value;
    if (props.attributeType === 'conversation_attribute') {
      await store.dispatch('updateCustomAttributes', {
        conversationId: conversationId.value,
        customAttributes: updatedAttributes,
      });
    } else {
      store.dispatch('contacts/deleteCustomAttributes', {
        id: props.contactId,
        customAttributes: [key],
      });
    }
    useAlert(t('CUSTOM_ATTRIBUTES.FORM.DELETE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message || t('CUSTOM_ATTRIBUTES.FORM.DELETE.ERROR');
    useAlert(errorMessage);
  }
};

const onCopy = async attributeValue => {
  await copyTextToClipboard(attributeValue);
  useAlert(t('CUSTOM_ATTRIBUTES.COPY_SUCCESSFUL'));
};

onMounted(() => {
  initializeSettings();
});

const evenClass = [
  '[&>*:nth-child(odd)]:!bg-transparent [&>*:nth-child(even)]:!bg-surface-raised',
  'dark:[&>*:nth-child(odd)]:!bg-transparent dark:[&>*:nth-child(even)]:!bg-surface-raised',
];
const oddClass = [
  '[&>*:nth-child(odd)]:!bg-surface-raised [&>*:nth-child(even)]:!bg-transparent',
  'dark:[&>*:nth-child(odd)]:!bg-surface-raised dark:[&>*:nth-child(even)]:!bg-transparent',
];

// O zebrado existe para separar uma linha da outra quando elas sao so texto
// solto. No desenho novo cada campo ja tem caixa e divisoria, e a faixa cinza
// alternada brigaria com o fundo da propria caixa.
const wrapperClass = computed(() => {
  if (props.panel) return [];
  return props.startAt === 'even' ? evenClass : oddClass;
});

// #1243: a seção de campos ganhou o atalho para criar um campo novo. O gate é o
// mesmo da rota de destino (`meta.permissions` de `attributes.routes.js`) —
// sem ele o botão levaria a uma tela que o próprio router recusa.
const canCreateAttribute = computed(() =>
  hasPermissions(
    ['administrator', 'custom_attribute_create'],
    getUserPermissions(
      getters.getCurrentUser.value,
      getters.getCurrentAccountId.value
    )
  )
);

// A tela de campos personalizados é uma só, com abas locais e modal de criação:
// qual aba abrir (conversa ou contato) e o pedido de abrir o modal viajam na
// query, e quem os lê é o `Index.vue` de `settings/attributes`.
const onCreateAttribute = () => {
  router.push({
    name: 'attributes_list',
    params: { accountId: String(getters.getCurrentAccountId.value) },
    query: { model: props.attributeType, new: '1' },
  });
};
</script>

<!-- TODO: After migration to Vue 3, remove the top level div -->
<template>
  <div>
    <!-- O zebrado vive num div PRÓPRIO: os seletores `nth-child` dele pintam
         todo filho direto, e o botão de criar campo acrescentado abaixo sairia
         com a faixa de fundo de um atributo. -->
    <div :class="wrapperClass">
      <CustomAttribute
        v-for="attribute in displayedAttributes"
        :key="attribute.id"
        :attribute-key="attribute.attribute_key"
        :attribute-type="attribute.attribute_display_type"
        :attribute-currency="attribute.attribute_currency || ''"
        :values="attribute.attribute_values"
        :label="attribute.attribute_display_name"
        :description="attribute.attribute_description"
        :value="attribute.value"
        show-actions
        :attribute-regex="attribute.regex_pattern"
        :regex-cue="attribute.regex_cue"
        :contact-id="contactId"
        :panel="panel"
        class="border-b border-solid border-line-faint"
        @update="onUpdate"
        @delete="onDelete"
        @copy="onCopy"
      />
      <PanelEmptyState
        v-if="!displayedAttributes.length && emptyStateMessage"
        :message="emptyStateMessage"
        :action-label="
          canCreateAttribute
            ? t('CONVERSATION_CUSTOM_ATTRIBUTES.ADD_BUTTON_TEXT')
            : ''
        "
        @action="onCreateAttribute"
      />
      <!-- Show more and show less buttons show it if the filteredAttributes length is greater than 5 -->
      <div v-if="filteredAttributes.length > 5" class="flex px-2 py-2">
        <woot-button
          size="small"
          :icon="showAllAttributes ? 'chevron-up' : 'chevron-down'"
          variant="clear"
          color-scheme="primary"
          class="!px-2 hover:!bg-transparent dark:hover:!bg-transparent"
          @click="onClickToggle"
        >
          {{ toggleButtonText }}
        </woot-button>
      </div>
    </div>
    <!-- Só com a lista cheia: no vazio o atalho já é a ação da moldura
         tracejada acima, e dois botões iguais colados leem como erro. -->
    <PanelAddButton
      v-if="canCreateAttribute && displayedAttributes.length"
      class="mt-2"
      :label="t('CONVERSATION_CUSTOM_ATTRIBUTES.ADD_BUTTON_TEXT')"
      @click="onCreateAttribute"
    />
  </div>
</template>
